/**
 * Writing Studio Context
 *
 * Provides project repository for the Writing Studio feature.
 * For now, only supports local storage. Cloud support will be added later.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { useFamily } from './FamilyContext'
import { useStudio } from './StudioContext'
import type { IProjectRepository } from '@/repositories/projectTypes'
import { localProjectRepository, setCurrentProjectChild } from '@/repositories/localProjectRepository'
import { getStorageHealth, clearStorageError, type StorageHealthState } from '@/services/localProjectStore'

interface WritingStudioContextValue {
  /** Project repository (local only for now) */
  repository: IProjectRepository
  /** Whether writes are disabled (lapsed subscription) */
  readOnly: boolean
  /** Whether the context is still loading */
  isLoading: boolean
  /** Storage health state */
  storageHealth: StorageHealthState
  /** Dismiss the storage error */
  dismissStorageError: () => void
  /** Refresh storage health (call after save operations) */
  refreshStorageHealth: () => void
}

const WritingStudioContext = createContext<WritingStudioContextValue | undefined>(undefined)

export function WritingStudioProvider({ children }: { children: ReactNode }) {
  const { activeWriter, isLoading: familyLoading } = useFamily()
  const { readOnly } = useStudio()

  // Storage health tracking
  const [storageHealth, setStorageHealth] = useState<StorageHealthState>(getStorageHealth)

  const refreshStorageHealth = useCallback(() => {
    setStorageHealth(getStorageHealth())
  }, [])

  const dismissStorageError = useCallback(() => {
    clearStorageError()
    setStorageHealth(getStorageHealth())
  }, [])

  // Set the current child context for the repository
  useEffect(() => {
    setCurrentProjectChild(activeWriter?.id ?? null)
    return () => setCurrentProjectChild(null)
  }, [activeWriter?.id])

  // For now, always use local repository
  // In the future, we can add cloud support similar to StudioContext
  const repository = useMemo(() => localProjectRepository, [])

  return (
    <WritingStudioContext.Provider
      value={{
        repository,
        readOnly,
        isLoading: familyLoading,
        storageHealth,
        dismissStorageError,
        refreshStorageHealth,
      }}
    >
      {children}
    </WritingStudioContext.Provider>
  )
}

export function useWritingStudio() {
  const context = useContext(WritingStudioContext)
  if (context === undefined) {
    throw new Error('useWritingStudio must be used within a WritingStudioProvider')
  }
  return context
}

/**
 * Convenience hook to get just the project repository
 */
export function useProjectRepository() {
  const { repository } = useWritingStudio()
  return repository
}
